/**
 * B2B bike configurator: state -> price -> form fields, as plain functions without DOM or Alpine
 * (tested with `npm test`, rendered by components/configurator.js).
 *
 * `cfg` is the server payload (ConfiguratorService), normalized and frozen by createConfig().
 * `sel` is the user's selection, the only mutable thing:
 *
 *   { frameId, variantId,           chosen frame (colour) and its size variant
 *     kits:  { <slug>: kitId },     base kits (always) and chosen slot kits
 *     dims:  { <kitId>: { <dimKey>: value } } }   variation values of kits that have variations
 *
 * Prices are only for display; the server validates the posted variant ids and takes the prices from the catalog.
 */

const asMap = (value) => (Array.isArray(value) || value == null ? {} : value);
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export function createConfig(payload) {
    const cfg = {
        buildId: payload.buildId,
        hasScope: Boolean(payload.hasScope),
        modelTag: payload.modelTag || '',
        frames: payload.frames || [],
        baseKits: payload.baseKits || [],
        slots: payload.slots || [],
        variations: asMap(payload.variations),
        skuMap: asMap(payload.skuMap),
        kitDefaults: asMap(payload.kitDefaults),
        promo: payload.promo || null,   // Aktion des Builds: { percent, until, dealers }
    };

    // kits per slot by id, for quick lookups
    cfg.slotKits = {};
    for (const slot of cfg.slots) {
        cfg.slotKits[slot.slug] = Object.fromEntries(slot.kits.map((kit) => [kit.kitId, kit]));
    }
    cfg.baseBySlug = Object.fromEntries(cfg.baseKits.map((kit) => [kit.slug, kit]));

    return deepFreeze(cfg);
}

function deepFreeze(obj) {
    Object.values(obj).forEach((v) => {
        if (v && typeof v === 'object' && !Object.isFrozen(v)) deepFreeze(v);
    });
    return Object.freeze(obj);
}

/* ---------- selection ---------- */

export function createSelection(cfg) {
    const sel = { frameId: null, variantId: null, kits: {}, dims: {} };

    // first colour that has an orderable size; its default size, else the first orderable one
    const frame = cfg.frames.find((f) => f.variants.some((v) => v.orderable)) || cfg.frames[0];
    if (frame) {
        sel.frameId = frame.id;
        const variants = frame.variants.filter((v) => v.orderable);
        sel.variantId = (variants.find((v) => v.isDefault) || variants[0] || {}).id ?? null;
    }

    for (const kit of cfg.baseKits) sel.kits[kit.slug] = kit.kitId;
    for (const slot of cfg.slots) {
        const preset = slot.kits.find((kit) => kit.isDefault && kit.orderable);
        if (preset) sel.kits[slot.slug] = preset.kitId;
    }

    applyDefaults(cfg, sel);

    return sel;
}

export function frameOf(cfg, sel) {
    return cfg.frames.find((f) => f.id === sel.frameId) || null;
}

export function variantOf(cfg, sel) {
    return frameOf(cfg, sel)?.variants.find((v) => v.id === sel.variantId) || null;
}

/** Another colour: keep the size (by label) when the new frame has it, else its default size. */
export function selectFrame(cfg, sel, frameId) {
    const frame = cfg.frames.find((f) => f.id === Number(frameId));
    if (!frame) return;
    const label = variantOf(cfg, sel)?.label;
    const orderable = frame.variants.filter((v) => v.orderable);
    const next = orderable.find((v) => v.label === label) || orderable.find((v) => v.isDefault) || orderable[0];
    sel.frameId = frame.id;
    sel.variantId = next ? next.id : null;
    sel.dims = {};
    applyDefaults(cfg, sel);
}

export function selectSize(cfg, sel, variantId) {
    const variant = frameOf(cfg, sel)?.variants.find((v) => v.id === Number(variantId));
    if (!variant || !variant.orderable) return;
    sel.variantId = variant.id;
    sel.dims = {};
    applyDefaults(cfg, sel);
}

/** Choose the kit of a slot (kitId null or 0 = none). Only the new kit gets its default variation values. */
export function selectKit(cfg, sel, slug, kitId) {
    const slot = cfg.slotKits[slug];
    if (!slot) return;
    const previous = sel.kits[slug];
    if (previous) delete sel.dims[previous];
    const kit = slot[Number(kitId)];
    if (!kit) {
        delete sel.kits[slug];
        return;
    }
    sel.kits[slug] = kit.kitId;
    applyDefaults(cfg, sel, kit.kitId);
}

export function setDim(sel, kitId, key, value) {
    sel.dims[kitId] = { ...(sel.dims[kitId] || {}), [key]: String(value ?? '') };
}

/** Default variation values (per category, model and frame size, else the first value) for all or one active kit. */
export function applyDefaults(cfg, sel, onlyKitId = null) {
    const size = variantOf(cfg, sel)?.label;
    for (const [slug, kitId] of Object.entries(sel.kits)) {
        if (onlyKitId !== null && kitId !== onlyKitId) continue;
        const variation = cfg.variations[kitId];
        if (!variation) continue;
        const defaults = cfg.kitDefaults[variation.typeSlug || slug]?.[cfg.modelTag]?.[size] || {};
        const dims = {};
        for (const dim of variation.dims) {
            const wanted = defaults[dim.key];
            const exists = dim.values.some((v) => String(v.value) === String(wanted));
            dims[dim.key] = String(exists ? wanted : (dim.values[0]?.value ?? ''));
        }
        sel.dims[kitId] = dims;
    }
}

/** Restore a configuration from the server ("?c=<hash>", ConfigurationService::preselect). */
export function applyPreselect(cfg, sel, pre) {
    if (!pre) return;
    if (pre.frameVariantId) {
        const frame = cfg.frames.find((f) => f.variants.some((v) => v.id === pre.frameVariantId));
        if (frame) {
            sel.frameId = frame.id;
            sel.variantId = pre.frameVariantId;
        }
    }
    sel.dims = {};
    // slots not in the saved configuration were "none"; base kits always stay
    for (const slot of cfg.slots) delete sel.kits[slot.slug];
    applyDefaults(cfg, sel);
    for (const [slug, saved] of Object.entries(pre.kits || {})) {
        if (cfg.slotKits[slug]?.[saved.kitId]) sel.kits[slug] = saved.kitId;
        else if (cfg.baseBySlug[slug]?.kitId !== saved.kitId) continue;
        applyDefaults(cfg, sel, saved.kitId);
        for (const [key, value] of Object.entries(saved.dims || {})) setDim(sel, saved.kitId, key, value);
    }
}

/* ---------- derived values ---------- */

function kitRow(cfg, slug, kitId) {
    return cfg.baseBySlug[slug]?.kitId === kitId ? cfg.baseBySlug[slug] : cfg.slotKits[slug]?.[kitId];
}

/** Every chosen kit resolved to the variant that goes into the cart (variation values -> SKU -> variant). */
export function items(cfg, sel) {
    const out = [];
    const order = [...cfg.baseKits.map((k) => k.slug), ...cfg.slots.map((s) => s.slug)];
    for (const slug of order) {
        const kitId = sel.kits[slug];
        const row = kitId && kitRow(cfg, slug, kitId);
        if (!row) continue;

        const variation = cfg.variations[kitId];
        const item = { slug, group: cfg.baseBySlug[slug] ? 'base' : 'slot', kitId, title: row.title, label: '' };
        if (variation) {
            const chosen = sel.dims[kitId] || {};
            const parts = variation.dims.map((dim) => chosen[dim.key] ?? '');
            const sku = `${variation.suffix || 'k'}.${kitId}.${parts.join('-')}`;
            const hit = parts.every(Boolean) ? cfg.skuMap[sku] : null;
            item.label = variation.dims
                .map((dim) => dim.values.find((v) => String(v.value) === String(chosen[dim.key]))?.label)
                .filter(Boolean)
                .join(' × ');
            Object.assign(item, hit && hit.available
                ? { variantId: hit.id, hek: hit.hek, uvp: hit.uvp, hekWas: hit.hekWas ?? hit.hek, uvpWas: hit.uvpWas ?? hit.uvp, available: true }
                : { variantId: null, hek: 0, uvp: 0, hekWas: 0, uvpWas: 0, available: false });
        } else {
            Object.assign(item, { variantId: row.variantId, hek: row.hek, uvp: row.uvp, hekWas: row.hekWas ?? row.hek, uvpWas: row.uvpWas ?? row.uvp, available: Boolean(row.orderable) });
        }
        out.push(item);
    }

    return out;
}

/** hek / uvp: what is paid (with the promotion of the build); hekWas / uvpWas: the same without it. */
export function totals(cfg, sel) {
    const frame = variantOf(cfg, sel);
    let hek = frame ? frame.hek : 0;
    let uvp = frame ? frame.uvp : 0;
    let hekWas = frame ? (frame.hekWas ?? frame.hek) : 0;
    let uvpWas = frame ? (frame.uvpWas ?? frame.uvp) : 0;
    for (const item of items(cfg, sel)) {
        if (!item.available) continue;
        hek += item.hek;
        uvp += item.uvp;
        hekWas += item.hekWas;
        uvpWas += item.uvpWas;
    }

    return { hek: round2(hek), uvp: round2(uvp), hekWas: round2(hekWas), uvpWas: round2(uvpWas) };
}

/** What stops "add to cart": no frame size, or a kit variation that is not available. */
export function problems(cfg, sel) {
    const list = [];
    if (!variantOf(cfg, sel)) list.push({ type: 'frame' });
    for (const item of items(cfg, sel)) {
        if (!item.available) list.push({ type: 'unavailable', slug: item.slug, title: item.title });
    }

    return list;
}

/** The only things the browser posts: frame variant and kit variants. */
export function formFields(cfg, sel) {
    const fields = [];
    if (sel.variantId) fields.push(['frame', sel.variantId]);
    for (const item of items(cfg, sel)) {
        if (item.variantId) fields.push([`kits[${item.slug}]`, item.variantId]);
    }

    return fields;
}

export function overviewTitle(cfg, sel, modelTitle) {
    const colour = frameOf(cfg, sel)?.identifier;
    return [modelTitle, colour, variantOf(cfg, sel)?.label].filter(Boolean).join(' - ');
}
